import React, { useState, useEffect, useMemo } from "react";
import { getCompanyUsers, updateUserById, updateUserSettings } from "../../utils/firestoreService";
import { getCompanyAdminUserDocumentActivity } from "../../utils/statsService";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { FileText, Search } from "lucide-react";
import { documentsPerPage } from "../../globals";
import { ROLE_LABELS, meetsMinRole, getAssignableRoles, getEffectiveInviteCap } from "../../utils/roleUtils";

const CompanyAdminUsers = ({ companyId, userId, adminPrefs, setAdminPrefs }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [updating, setUpdating] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [userDocumentActivity, setUserDocumentActivity] = useState(null);

  // Filters
  const [roleFilter, setRoleFilter] = useState(adminPrefs?.usersRoleFilter ?? "all");
  const [statusFilter, setStatusFilter] = useState(adminPrefs?.usersStatusFilter ?? "all");

  // Sort
  const [sortConfig, setSortConfig] = useState({
    key: adminPrefs?.usersSortKey ?? "displayName",
    direction: adminPrefs?.usersSortDirection ?? "asc",
  });

  // Confirmation dialogs
  const [roleChangeDialog, setRoleChangeDialog] = useState(null); // { user, newRole }
  const [deactivateDialog, setDeactivateDialog] = useState(null); // user

  // User detail dialog
  const [detailUser, setDetailUser] = useState(null);

  const assignableRoles = getAssignableRoles("companyAdmin"); // ['viewer','clinician','standard','manager']

  // Fetch company users
  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setIsLoading(true);
      setUsers([]);
      setUserDocumentActivity(null);
      try {
        const [companyUsers, activityResult] = await Promise.all([
          getCompanyUsers(companyId),
          getCompanyAdminUserDocumentActivity()
            .then((data) => ({ data, failed: false }))
            .catch((error) => {
              console.error("Error fetching weekly company user document activity:", error);
              return { data: null, failed: true };
            }),
        ]);
        if (cancelled) return;
        setUsers(companyUsers);
        setUserDocumentActivity(activityResult.failed ? null : activityResult.data);
      } catch (error) {
        console.error("Error fetching company users:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    if (companyId) {
      fetchUsers();
    } else {
      setUsers([]);
      setUserDocumentActivity(null);
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter]);

  // Persist filter preferences
  const updateFilterPref = (key, value) => {
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, [key]: value };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { companyAdminPreferences: updatedPrefs });
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === "asc";
    const newDirection = isAsc ? "desc" : "asc";
    setSortConfig({ key: property, direction: newDirection });
    updateFilterPref("usersSortKey", property);
    updateFilterPref("usersSortDirection", newDirection);
  };

  // --- Role change ---
  const handleRoleChangeRequest = (user, newRole) => {
    setRoleChangeDialog({ user, newRole });
  };

  const confirmRoleChange = async () => {
    if (!roleChangeDialog) return;
    const { user, newRole } = roleChangeDialog;
    setRoleChangeDialog(null);
    setUpdating((prev) => ({ ...prev, [user.id]: true }));
    try {
      await updateUserById(user.id, { userRole: newRole });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, userRole: newRole } : u)));
    } catch (error) {
      console.error("Error updating user role:", error);
    }
    setUpdating((prev) => ({ ...prev, [user.id]: false }));
  };

  // --- Deactivate / Reactivate ---
  const handleToggleActive = (user) => {
    if (user.disabled) {
      // Reactivate immediately (no confirmation needed)
      performToggleActive(user);
    } else {
      // Deactivate needs confirmation
      setDeactivateDialog(user);
    }
  };

  const performToggleActive = async (user) => {
    setDeactivateDialog(null);
    setUpdating((prev) => ({ ...prev, [user.id]: true }));
    try {
      const newDisabled = !user.disabled;
      await updateUserById(user.id, { disabled: newDisabled });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, disabled: newDisabled } : u)));
    } catch (error) {
      console.error("Error updating user status:", error);
    }
    setUpdating((prev) => ({ ...prev, [user.id]: false }));
  };

  // --- Helpers ---
  const isHigherRole = (userRole) => meetsMinRole(userRole, "companyAdmin");
  const isSelf = (uid) => uid === userId;

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "Never";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getDisplayName = (user) =>
    user.displayName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Unnamed";

  // --- Filtering ---
  const filteredUsers = users.filter((user) => {
    if (roleFilter !== "all" && (user.userRole || "standard") !== roleFilter) return false;
    if (statusFilter === "active" && user.disabled) return false;
    if (statusFilter === "deactivated" && !user.disabled) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match =
        user.email?.toLowerCase().includes(q) ||
        user.displayName?.toLowerCase().includes(q) ||
        user.firstName?.toLowerCase().includes(q) ||
        user.lastName?.toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  // --- Sorting ---
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case "displayName":
          aVal = getDisplayName(a).toLowerCase();
          bVal = getDisplayName(b).toLowerCase();
          break;
        case "email":
          aVal = (a.email || "").toLowerCase();
          bVal = (b.email || "").toLowerCase();
          break;
        case "userRole":
          aVal = a.userRole || "standard";
          bVal = b.userRole || "standard";
          break;
        case "disabled":
          aVal = a.disabled ? 1 : 0;
          bVal = b.disabled ? 1 : 0;
          break;
        case "lastLoginTime":
          aVal = a.lastLoginTime?.toDate ? a.lastLoginTime.toDate().getTime() : new Date(a.lastLoginTime || 0).getTime();
          bVal = b.lastLoginTime?.toDate ? b.lastLoginTime.toDate().getTime() : new Date(b.lastLoginTime || 0).getTime();
          break;
        case "createdAt":
          aVal = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
          bVal = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
          break;
        default:
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
      }
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  // --- Pagination ---
  const indexOfLast = currentPage * documentsPerPage;
  const indexOfFirst = indexOfLast - documentsPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedUsers.length / documentsPerPage);

  return (
    <div className="standard-container" style={{ marginBottom: "60px" }}>
      {/* Filters */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <div className="home-header-search-wrapper" style={{ width: "auto" }}>
          <Tooltip title="Search users" arrow>
            <Search className="header-search-icon" size={18} />
          </Tooltip>
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="header-search-input"
          />
        </div>

        <Box className="company-admin-filters">
          <Select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); updateFilterPref("usersRoleFilter", e.target.value); }}
            size="small"
            sx={{ minWidth: 120, fontSize: "var(--text-xs)", "& .MuiSelect-select": { py: 0.75 } }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {assignableRoles.map((role) => (
              <MenuItem key={role} value={role}>{ROLE_LABELS[role]}</MenuItem>
            ))}
          </Select>

          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); updateFilterPref("usersStatusFilter", e.target.value); }}
            size="small"
            sx={{ minWidth: 120, fontSize: "var(--text-xs)", "& .MuiSelect-select": { py: 0.75 } }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="deactivated">Deactivated</MenuItem>
          </Select>
        </Box>
      </Box>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid var(--color-border)" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "var(--color-background-alt)" }}>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={sortConfig.key === "displayName"} direction={sortConfig.key === "displayName" ? sortConfig.direction : "asc"} onClick={() => handleRequestSort("displayName")}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={sortConfig.key === "email"} direction={sortConfig.key === "email" ? sortConfig.direction : "asc"} onClick={() => handleRequestSort("email")}>
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={sortConfig.key === "userRole"} direction={sortConfig.key === "userRole" ? sortConfig.direction : "asc"} onClick={() => handleRequestSort("userRole")}>
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={sortConfig.key === "disabled"} direction={sortConfig.key === "disabled" ? sortConfig.direction : "asc"} onClick={() => handleRequestSort("disabled")}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={sortConfig.key === "lastLoginTime"} direction={sortConfig.key === "lastLoginTime" ? sortConfig.direction : "asc"} onClick={() => handleRequestSort("lastLoginTime")}>
                  Last Login
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel active={sortConfig.key === "createdAt"} direction={sortConfig.key === "createdAt" ? sortConfig.direction : "asc"} onClick={() => handleRequestSort("createdAt")}>
                  Joined
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {searchQuery || roleFilter !== "all" || statusFilter !== "all"
                    ? "No users match your current filters"
                    : "No users found"}
                </TableCell>
              </TableRow>
            ) : (
              currentUsers.map((user) => {
                const userIsHigherRole = isHigherRole(user.userRole);
                const userIsSelf = isSelf(user.id);
                const canModify = !userIsSelf && !userIsHigherRole;
                const documentActivity = userDocumentActivity?.[user.id] || {
                  documentsCreated: 0,
                  patientsDocumented: 0,
                  lastDocumentAt: null,
                };
                const lastDocumentDate = documentActivity.lastDocumentAt
                  ? new Date(documentActivity.lastDocumentAt)
                  : null;
                const activityLabel = documentActivity.documentsCreated > 0
                  ? `${documentActivity.documentsCreated} document${documentActivity.documentsCreated === 1 ? "" : "s"} created in the last 7 days; ${documentActivity.patientsDocumented} patient${documentActivity.patientsDocumented === 1 ? "" : "s"} documented${lastDocumentDate ? `; last document ${lastDocumentDate.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}`
                  : "No documents created in the last 7 days";

                return (
                  <TableRow
                    key={user.id}
                    sx={{
                      opacity: user.disabled ? 0.5 : 1,
                      backgroundColor: userIsSelf ? "var(--color-highlight-light)" : "inherit",
                    }}
                  >
                    <TableCell>
                      <span
                        style={{ cursor: "pointer", color: "var(--color-primary)", fontWeight: 500 }}
                        onClick={() => setDetailUser(user)}
                      >
                        {getDisplayName(user)}
                      </span>
                      {userDocumentActivity && (
                        <Tooltip
                          title={(
                            <Box sx={{ py: 0.25 }}>
                              <Box sx={{ fontWeight: 600, mb: 0.5 }}>Last 7 days</Box>
                              <Box>Documents created: {documentActivity.documentsCreated}</Box>
                              <Box>Patients documented: {documentActivity.patientsDocumented}</Box>
                              {lastDocumentDate && (
                                <Box>
                                  Last document: {lastDocumentDate.toLocaleString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })}
                                </Box>
                              )}
                            </Box>
                          )}
                          arrow
                        >
                          <Box
                            component="span"
                            role="img"
                            tabIndex={0}
                            aria-label={activityLabel}
                            sx={{
                              display: "inline-flex",
                              verticalAlign: "middle",
                              ml: 0.75,
                              color: documentActivity.documentsCreated > 0
                                ? "var(--color-primary)"
                                : "var(--color-text-secondary)",
                              opacity: documentActivity.documentsCreated > 0 ? 0.9 : 0.4,
                              cursor: "help",
                            }}
                          >
                            <FileText size={14} aria-hidden="true" />
                          </Box>
                        </Tooltip>
                      )}
                      {userIsSelf && (
                        <span style={{ fontSize: "var(--text-xs)", marginLeft: "8px", color: "var(--color-primary)", fontWeight: 500 }}>
                          (You)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="patient-details-small">{user.email}</span>
                    </TableCell>
                    <TableCell>
                      {canModify ? (
                        <Select
                          value={user.userRole || "standard"}
                          onChange={(e) => handleRoleChangeRequest(user, e.target.value)}
                          size="small"
                          disabled={updating[user.id]}
                          sx={{ minWidth: 120, fontSize: "var(--text-sm)" }}
                        >
                          {assignableRoles.map((role) => (
                            <MenuItem key={role} value={role}>{ROLE_LABELS[role]}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <Chip
                          label={ROLE_LABELS[user.userRole] || user.userRole || "Standard"}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.disabled ? "Deactivated" : "Active"}
                        size="small"
                        className={user.disabled ? "company-admin-status-deactivated" : "company-admin-status-active"}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="patient-details-small">{formatRelativeTime(user.lastLoginTime)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="patient-details-small">{formatDate(user.createdAt)}</span>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={userIsSelf ? "Cannot deactivate yourself" : userIsHigherRole ? "Cannot modify admin users" : user.disabled ? "Reactivate user" : "Deactivate user"} arrow>
                        <span>
                          <Switch
                            checked={!user.disabled}
                            onChange={() => handleToggleActive(user)}
                            disabled={updating[user.id] || !canModify}
                            color="primary"
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary + pagination */}
      <div style={{ marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="company-admin-user-count">
          Showing {filteredUsers.length > 0 ? indexOfFirst + 1 : 0}-{Math.min(indexOfLast, filteredUsers.length)} of {filteredUsers.length} users
          {(roleFilter !== "all" || statusFilter !== "all" || searchQuery) && ` (filtered from ${users.length} total)`}
        </span>
        {totalPages > 1 && (
          <Pagination count={totalPages} page={currentPage} onChange={(e, v) => setCurrentPage(v)} color="primary" variant="outlined" shape="rounded" size="small" />
        )}
      </div>

      {/* Role Change Confirmation Dialog */}
      <Dialog open={!!roleChangeDialog} onClose={() => setRoleChangeDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Change Role</DialogTitle>
        <DialogContent>
          Change <strong>{roleChangeDialog && getDisplayName(roleChangeDialog.user)}</strong>'s role to{" "}
          <strong>{roleChangeDialog && ROLE_LABELS[roleChangeDialog.newRole]}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleChangeDialog(null)}>Cancel</Button>
          <Button onClick={confirmRoleChange} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={!!deactivateDialog} onClose={() => setDeactivateDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          Are you sure you want to deactivate <strong>{deactivateDialog && getDisplayName(deactivateDialog)}</strong>?
          They will no longer be able to log in.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog(null)}>Cancel</Button>
          <Button onClick={() => performToggleActive(deactivateDialog)} variant="contained" color="error">Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* User Detail Dialog */}
      <Dialog open={!!detailUser} onClose={() => setDetailUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        {detailUser && (
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Name</Box>
                <Box sx={{ fontWeight: 500 }}>{getDisplayName(detailUser)}</Box>
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Email</Box>
                <Box>{detailUser.email}</Box>
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Role</Box>
                {!isSelf(detailUser.id) && !isHigherRole(detailUser.userRole) ? (
                  <Select
                    value={detailUser.userRole || "standard"}
                    onChange={(e) => {
                      handleRoleChangeRequest(detailUser, e.target.value);
                    }}
                    size="small"
                    disabled={updating[detailUser.id]}
                    sx={{ minWidth: 140 }}
                  >
                    {assignableRoles.map((role) => (
                      <MenuItem key={role} value={role}>{ROLE_LABELS[role]}</MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Chip label={ROLE_LABELS[detailUser.userRole] || detailUser.userRole} size="small" variant="outlined" />
                )}
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Status</Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={detailUser.disabled ? "Deactivated" : "Active"}
                    size="small"
                    className={detailUser.disabled ? "company-admin-status-deactivated" : "company-admin-status-active"}
                  />
                  {!isSelf(detailUser.id) && !isHigherRole(detailUser.userRole) && (
                    <Switch
                      checked={!detailUser.disabled}
                      onChange={() => handleToggleActive(detailUser)}
                      disabled={updating[detailUser.id]}
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Last Login</Box>
                <Box>{formatRelativeTime(detailUser.lastLoginTime)}</Box>
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Joined</Box>
                <Box>{formatDate(detailUser.createdAt)}</Box>
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Access Type</Box>
                <Box>{detailUser.accessType || "standard"}</Box>
              </Box>
              <Box>
                <Box sx={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", mb: 0.5 }}>Invites</Box>
                <Box>{detailUser.invitesSent || 0} / {getEffectiveInviteCap(detailUser)}</Box>
              </Box>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDetailUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CompanyAdminUsers;
